import { useState, useEffect, useContext } from 'react';
import { usePoll } from '../../util/usePoll';
import { EntityAboutCard } from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, errorApiRef } from '@backstage/core-plugin-api';
import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
} from '@devxdiscover/devhub-ui';
import { useQuery, gql } from '@apollo/client';
import { parseEntityRef } from '@backstage/catalog-model';
import { cacheDelete, cacheGet, cacheSet } from '../../util/persistedState.ts';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck.tsx';
import { Feature } from '@devxdiscover/devhub-common';
import {
  useGroupMembership,
  UserContext,
} from '../../contexts/UserContext.tsx';

const GET_APPLICATION_POLICY = gql`
  query GetApplicationPolicy(
    $applicationId: Int!
    $filter: AuthorizationPolicyFilter!
  ) {
    resources {
      application(applicationId: $applicationId) {
        authorizationPolicy(filter: $filter) {
          policy
          hasAccess
        }
      }
    }
  }
`;

/**
 * EntityAboutCardWithRefresh component
 * @param props - The props for the component
 * @constructor
 */
export const EntityAboutCardWithRefresh = (props: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const { login, hasGroupMembership } = useContext(UserContext);
  const config = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const entityKey = entity.metadata.uid || entity.metadata.name;
  const [isRefreshing, setIsRefreshing] = useState(
    () => !!cacheGet<string>(entityKey),
  );

  const [canRefresh, setCanRefresh] = useState(false);
  const [canRefreshAdmin, setCanRefreshAdmin] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [isCheckingAdminPermissions, setIsCheckingAdminPermissions] =
    useState(true);
  const [pendingRunId, setPendingRunId] = useState<string | undefined>(
    () => cacheGet<string>(entityKey) ?? undefined,
  );

  const backendUrl = config.getString('backend.baseUrl');
  const onlyAdmins =
    config.getOptionalBoolean('ondemandRefresh.onlyAdmins') ?? true;

  usePoll({
    enabled: !!pendingRunId,
    fn: async () => {
      const response = await fetch(
        `${backendUrl}/api/fusionUtil/isRunning/${pendingRunId}`,
      );
      if (!response.ok) throw new Error(response.statusText);
      const { isRunning } = await response.json();
      return !isRunning;
    },
    onDone: () => {
      cacheDelete(entityKey);
      setPendingRunId(undefined);
      setIsRefreshing(false);
    },
  });
  const {
    value: groupData,
    loading: isLoading,
    error: isError,
  } = useGroupMembership(login, ['[CHQ -- DevHub]']);
  useEffect(() => {
    if (groupData && hasGroupMembership(groupData, '[CHQ -- DevHub]')) {
      setIsCheckingAdminPermissions(false);
      setCanRefreshAdmin(true);
    }
    if (isLoading) {
      setIsCheckingAdminPermissions(true);
      return;
    }

    if (isError) {
      setCanRefreshAdmin(false);
      setIsCheckingAdminPermissions(false);
      return;
    }
  }, [groupData, hasGroupMembership, isLoading, isError]);

  // Extract application ID from entity spec.system
  // Format is: "system:default/59712" - need to extract the number at the end
  const systemRef = entity.spec?.system;

  const appId = systemRef
    ? Number(
        parseEntityRef(systemRef as string, {
          defaultKind: 'system',
          defaultNamespace: 'default',
        }).name,
      )
    : undefined;
  // Query the policy only if we have an applicationId
  const {
    data,
    loading: isLoadingPolicy,
    error,
  } = useQuery(GET_APPLICATION_POLICY, {
    skip: !appId,
    variables: {
      applicationId: appId,
      filter: {
        policies: ['UpdateApplicationPolicy'],
      },
    },
  });

  useEffect(() => {
    if (!appId) {
      setCanRefresh(true);
      setIsCheckingPermissions(false);
      return;
    }

    if (isLoadingPolicy) {
      setIsCheckingPermissions(true);
      return;
    }

    if (error) {
      setCanRefresh(false);
      setIsCheckingPermissions(false);
      return;
    }

    if (data?.resources?.application?.authorizationPolicy) {
      const updatePolicy = data.resources.application.authorizationPolicy.find(
        (p: any) => p.policy === 'UpdateApplicationPolicy',
      );
      setCanRefresh(updatePolicy?.hasAccess || false);
    } else {
      setCanRefresh(false);
    }

    setIsCheckingPermissions(false);
  }, [appId, data, isLoadingPolicy, error]);

  const handleRefresh = async () => {
    const metadata = entity.metadata;
    const annotations = metadata.annotations || {};
    const githubProjectSlug = annotations['github.com/project-slug'];

    if (!githubProjectSlug) {
      errorApi.post(
        new Error('No GitHub project slug found in entity annotations'),
      );
      return;
    }

    const [orgName, repoName] = githubProjectSlug.split('/');

    if (!orgName || !repoName) {
      errorApi.post(new Error('Invalid GitHub project slug format'));
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/fusionUtil/import/repo/${orgName}/${repoName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            componentId: entity.metadata.name,
            kind: entity.kind,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to refresh: ${response.statusText}`);
      }

      const responseData = await response.json();
      cacheSet(entityKey, responseData.runId, { minutes: 30 });
      setPendingRunId(responseData.runId);
    } catch (err) {
      errorApi.post(err as Error);
      setIsRefreshing(false);
    }
  };

  const isRunning = isRefreshing || !!pendingRunId;
  const canRefreshFlag = onlyAdmins
    ? !isCheckingAdminPermissions && canRefreshAdmin
    : !isCheckingPermissions && canRefresh;
  // Show the button if a refresh is in progress (disabled) OR the user has permission (enabled).
  // Hide it only when not running AND the user lacks permission (or check is still loading).
  const showRefreshButton = isRunning || canRefreshFlag;

  return (
    <DevHubBox position="relative" height="100%">
      <EntityAboutCard variant={props.variant} />
      <ConfigFeatureCheck feature={Feature.ONDEMAND_REFRESH}>
        {showRefreshButton && (
          <DevHubBox position="absolute" top={16} right={56} display="flex">
            <DevHubButton
              onClick={handleRefresh}
              disabled={isRunning}
              size="small"
              variant="outlined"
            >
              <DevHubDynamicIcon iconName="Refresh" />
              &nbsp;Refresh
            </DevHubButton>
          </DevHubBox>
        )}
      </ConfigFeatureCheck>
    </DevHubBox>
  );
};
